import * as XLSX from "xlsx";
import { PLAN_CATEGORIES } from "./planTypeMappingConstants";

const normaliseHeader = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const HEADER_ALIASES = {
  plantypeid: "planTypeId",
  id: "planTypeId",
  plantypename: "planTypeName",
  plantype: "planTypeName",
  rosemountcategory: "planCategory",
  plancategory: "planCategory",
  category: "planCategory",
  portfoliocategorythiscanbeadjustedinio: "portfolioCategory",
  portfoliocategory: "portfolioCategory",
  productgroupthiscanbeadjustedinio: "productGroup",
  productgroup: "productGroup",
};

export const normalisePlanTypeRows = (sourceRows = []) => {
  const seenIds = new Set();

  return sourceRows.map((sourceRow, index) => {
    const mapped = Object.entries(sourceRow || {}).reduce(
      (result, [header, value]) => {
        const field = HEADER_ALIASES[normaliseHeader(header)];
        if (field) result[field] = String(value ?? "").trim();
        return result;
      },
      {},
    );

    const errors = [];

    if (!mapped.planTypeId) errors.push("Plan type ID is required");
    if (!mapped.planTypeName) errors.push("Plan type name is required");
    if (!mapped.planCategory) errors.push("Plan category is required");
    if (!mapped.portfolioCategory)
      errors.push("Portfolio category is required");
    if (!mapped.productGroup) errors.push("Product group is required");
    if (mapped.planCategory && !PLAN_CATEGORIES.includes(mapped.planCategory)) {
      errors.push(
        `Plan category must be one of: ${PLAN_CATEGORIES.join(", ")}`,
      );
    }
    if (mapped.planTypeId && seenIds.has(mapped.planTypeId)) {
      errors.push("Duplicate plan type ID in this file");
    }

    if (mapped.planTypeId) seenIds.add(mapped.planTypeId);

    return {
      rowNumber: index + 2,
      planTypeId: mapped.planTypeId || "",
      planTypeName: mapped.planTypeName || "",
      planCategory: mapped.planCategory || "",
      portfolioCategory: mapped.portfolioCategory || "",
      productGroup: mapped.productGroup || "",
      errors,
      isValid: errors.length === 0,
    };
  });
};

export const parsePlanTypeMappingFile = async (file) => {
  if (!file) throw new Error("Choose a CSV or Excel file first.");

  const fileName = String(file.name || "").toLowerCase();
  if (!/\.(csv|xlsx|xls)$/.test(fileName)) {
    throw new Error("Use a .csv, .xlsx, or .xls file.");
  }

  const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
  const firstSheetName = workbook.SheetNames[0];

  if (!firstSheetName) throw new Error("The uploaded file has no worksheets.");

  const rows = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheetName], {
    defval: "",
    raw: false,
  });

  if (!rows.length) throw new Error("The uploaded file contains no data rows.");

  const parsedRows = normalisePlanTypeRows(rows);
  const recognised =
    parsedRows.some((row) => row.planTypeId) ||
    parsedRows.some((row) => row.planTypeName);

  if (!recognised) {
    throw new Error(
      'Required columns were not found. Use "Plan type ID", "Plan type name", and "Rosemount Category".',
    );
  }

  return parsedRows;
};
